import { useState } from 'react';
import { NavBar } from '../components/NavBar.tsx';
import { Calendar } from '../components/Calendar.tsx';
import { StatusPill, PriorityPill } from '../components/Pill.tsx';
import { useNotes } from '../api/notes.ts';
import type { PrayerNote } from '../domain.ts';
import { daysUntil, ddayLabel } from '../domain.ts';

/**
 * E1 · E2 — Prayer Diary.
 *
 * 상단 캘린더, 하단 리스트. 기본은 오늘 → 다가올 → 지난 순이고,
 * 날짜를 누르면 그 날짜로 좁혀집니다.
 *
 * Everyday 노트는 여기 나타나지 않습니다 (기획서 3.4).
 * 매일이면 달력에 표시할 의미가 없기 때문입니다.
 */
function isoToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function NoteCard({ note }: { note: PrayerNote }) {
  const date = note.plan.type === 'DATE' ? note.plan.date : null;
  return (
    <article className="card">
      <div className="card__pills">
        <StatusPill status={note.status} />
        {note.priority !== 'NORMAL' && <PriorityPill priority={note.priority} />}
        {date && (
          <span className="dday" data-priority={note.priority}>
            {ddayLabel(date)}
          </span>
        )}
      </div>
      <h3 className="card__title">{note.title}</h3>
      <p className="card__meta">{note.category}</p>
    </article>
  );
}

export function Diary() {
  const { notes } = useNotes();
  const today = isoToday();
  const now = new Date();
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [selected, setSelected] = useState<string | null>(null);

  // Everyday 제외 — 달력에도 리스트에도 나오지 않습니다
  const dated = notes.filter((n) => n.plan.type === 'DATE');

  const shift = (by: number) => {
    const d = new Date(cursor.year, cursor.month + by, 1);
    setCursor({ year: d.getFullYear(), month: d.getMonth() });
    setSelected(null);
  };

  const onDate = selected ? dated.filter((n) => n.plan.type === 'DATE' && n.plan.date === selected) : [];

  const todayNotes = dated.filter((n) => n.plan.type === 'DATE' && n.plan.date === today);
  const upcoming = dated
    .filter((n) => n.plan.type === 'DATE' && daysUntil(n.plan.date) > 0)
    .sort((a, b) =>
      a.plan.type === 'DATE' && b.plan.type === 'DATE' ? a.plan.date.localeCompare(b.plan.date) : 0,
    );
  const past = dated
    .filter((n) => n.plan.type === 'DATE' && daysUntil(n.plan.date) < 0)
    .sort((a, b) =>
      a.plan.type === 'DATE' && b.plan.type === 'DATE' ? b.plan.date.localeCompare(a.plan.date) : 0,
    );

  const selectedLabel = selected
    ? new Date(`${selected}T00:00:00`).toLocaleDateString('ko-KR', {
        month: 'long',
        day: 'numeric',
        weekday: 'long',
      })
    : '';

  return (
    <div className="screen">
      <NavBar
        title={`${cursor.year}년 ${cursor.month + 1}월`}
        start={{ icon: 'back', label: '이전 달', onClick: () => shift(-1) }}
        end={{ icon: 'forward', label: '다음 달', onClick: () => shift(1) }}
      />

      <div className="screen__body">
        <Calendar
          year={cursor.year}
          month={cursor.month}
          notes={dated}
          today={today}
          selected={selected}
          onSelect={(d) => setSelected((prev) => (prev === d ? null : d))}
        />

        <hr className="rule" />

        {selected ? (
          <>
            <div className="section-head">
              <h2 className="section-head__title">{selectedLabel}</h2>
              <button type="button" className="section-head__action" onClick={() => setSelected(null)}>
                선택 해제
              </button>
            </div>
            {onDate.length > 0 ? (
              onDate.map((n) => <NoteCard key={n.id} note={n} />)
            ) : (
              <div className="empty">
                <p className="empty__title">이 날짜에 기도 노트가 없습니다</p>
                <p className="empty__hint">
                  Prayer Book 에서 노트를 만들 때 Plan 을 이 날짜로 지정하면 여기 나타납니다.
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <Group title={`오늘 · ${new Date().getMonth() + 1}월 ${new Date().getDate()}일`} items={todayNotes} />
            <Group title="다가올 기도 노트" items={upcoming} />
            <Group title="지난 기도 노트" items={past} />
          </>
        )}
      </div>
    </div>
  );
}

function Group({ title, items }: { title: string; items: PrayerNote[] }) {
  if (items.length === 0) return null;
  return (
    <>
      <p className="section-title">
        {title} <span>{items.length}</span>
      </p>
      {items.map((n) => (
        <NoteCard key={n.id} note={n} />
      ))}
    </>
  );
}
